import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Hammer, Bath, Home, PaintBucket, Warehouse, DoorOpen, Layers, ArrowDown, Wrench, Trash, Plus, Trash2, Pencil } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  kitchen: Hammer, bathroom: Bath, roofing: Home, deck: Layers, painting: PaintBucket,
  general: Wrench, garage: Warehouse, flooring: Layers, basement: ArrowDown,
  windows: DoorOpen, demolition: Trash, custom: FileText,
};

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    project_bullets: [],
    sections: [{ title: 'Section 1', items: [{ description: '', qty: 1, unit_price: 0 }], contingencies: [] }],
    payment_terms: '',
    notes: '',
  });

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API}/templates`, { credentials: 'include' });
      if (res.ok) setTemplates(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleUseTemplate = async (template) => {
    try {
      const body = {
        title: `${template.name} Estimate`,
        project_bullets: template.project_bullets || [],
        sections: template.sections || [],
        payment_terms: template.payment_terms || '',
        notes: template.notes || '',
        status: 'draft',
        estimate_type: template.category || '',
      };
      const res = await fetch(`${API}/estimates`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(body),
      });
      if (res.ok) {
        const est = await res.json();
        toast.success('Estimate created from template');
        navigate(`/estimates/${est.estimate_id}`);
      }
    } catch { toast.error('Failed to create estimate'); }
  };

  const handleBlankEstimate = async () => {
    try {
      const body = {
        title: 'New Estimate', project_bullets: [],
        sections: [{ title: 'Section 1', items: [{ description: '', qty: 0, unit_price: 0 }, { description: 'Labour and Materials', qty: 1, unit_price: 0 }], contingencies: [] }],
        status: 'draft',
      };
      const res = await fetch(`${API}/estimates`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(body),
      });
      if (res.ok) {
        const est = await res.json();
        toast.success('Blank estimate created');
        navigate(`/estimates/${est.estimate_id}`);
      }
    } catch { toast.error('Failed to create estimate'); }
  };

  const resetForm = () => {
    setForm({
      name: '', project_bullets: [],
      sections: [{ title: 'Section 1', items: [{ description: '', qty: 1, unit_price: 0 }], contingencies: [] }],
      payment_terms: '', notes: '',
    });
    setEditTemplate(null);
  };

  const openCreate = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openEdit = (tpl, e) => {
    e.stopPropagation();
    setEditTemplate(tpl);
    setForm({
      name: tpl.name,
      project_bullets: tpl.project_bullets || [],
      sections: tpl.sections?.length ? tpl.sections : [{ title: 'Section 1', items: [{ description: '', qty: 1, unit_price: 0 }], contingencies: [] }],
      payment_terms: tpl.payment_terms || '',
      notes: tpl.notes || '',
    });
    setShowCreateDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!form.name.trim()) { toast.error('Enter a template name'); return; }
    const body = {
      name: form.name,
      category: 'custom',
      project_bullets: form.project_bullets.filter(b => b.trim()),
      sections: form.sections,
      payment_terms: form.payment_terms,
      notes: form.notes,
    };

    try {
      const res = await fetch(`${API}/templates`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success(editTemplate ? 'Template updated' : 'Template created');
        setShowCreateDialog(false);
        resetForm();
        fetchTemplates();
      } else { toast.error('Failed to save template'); }
    } catch { toast.error('Failed to save template'); }
  };

  const handleDeleteTemplate = async () => {
    if (!deleteId) return;
    try {
      await fetch(`${API}/templates/${deleteId}`, { method: 'DELETE', credentials: 'include' });
      setTemplates(prev => prev.filter(t => t.template_id !== deleteId));
      toast.success('Template deleted');
    } catch { toast.error('Failed to delete'); }
    setDeleteId(null);
  };

  // Form helpers
  const addFormSection = () => {
    setForm(prev => ({
      ...prev,
      sections: [...prev.sections, { title: `Section ${prev.sections.length + 1}`, items: [{ description: '', qty: 1, unit_price: 0 }], contingencies: [] }],
    }));
  };
  const removeFormSection = (idx) => {
    setForm(prev => ({ ...prev, sections: prev.sections.filter((_, i) => i !== idx) }));
  };
  const updateFormSection = (idx, field, value) => {
    setForm(prev => {
      const sections = [...prev.sections];
      sections[idx] = { ...sections[idx], [field]: value };
      return { ...prev, sections };
    });
  };
  const addFormItem = (sIdx) => {
    setForm(prev => {
      const sections = [...prev.sections];
      sections[sIdx] = { ...sections[sIdx], items: [...sections[sIdx].items, { description: '', qty: 1, unit_price: 0 }] };
      return { ...prev, sections };
    });
  };
  const removeFormItem = (sIdx, iIdx) => {
    setForm(prev => {
      const sections = [...prev.sections];
      sections[sIdx] = { ...sections[sIdx], items: sections[sIdx].items.filter((_, i) => i !== iIdx) };
      return { ...prev, sections };
    });
  };
  const updateFormItem = (sIdx, iIdx, field, value) => {
    setForm(prev => {
      const sections = [...prev.sections];
      const items = [...sections[sIdx].items];
      items[iIdx] = { ...items[iIdx], [field]: value };
      sections[sIdx] = { ...sections[sIdx], items };
      return { ...prev, sections };
    });
  };

  const presets = templates.filter(t => t.is_preset);
  const custom = templates.filter(t => !t.is_preset);

  return (
    <div className="animate-fade-in" data-testid="templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Templates
          </h1>
          <p className="text-sm text-[#6B7A73] mt-1">Choose a template to start a new estimate, or create your own.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="create-template-btn" onClick={openCreate} variant="outline" className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md px-5 py-2.5 font-medium">
            <Plus className="w-4 h-4 mr-2" /> Create Template
          </Button>
          <Button data-testid="blank-estimate-btn" onClick={handleBlankEstimate} className="bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md px-5 py-2.5 font-medium">
            <Plus className="w-4 h-4 mr-2" /> Blank Estimate
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center">
          <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : (
        <>
          {/* Preset Templates */}
          <div className="mb-8">
            <h2 className="text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7A73] mb-4">Pre-built Templates</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {presets.map((tpl) => {
                const Icon = CATEGORY_ICONS[tpl.category] || FileText;
                const sectionCount = tpl.sections?.length || 0;
                return (
                  <div key={tpl.template_id} className="bg-white border border-[#E6EBE8] rounded-md p-5 hover:-translate-y-0.5 hover:border-[#C8D4CE] transition-all duration-200 cursor-pointer group" onClick={() => handleUseTemplate(tpl)} data-testid={`template-${tpl.category}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-md bg-[rgba(45,107,104,0.1)] flex items-center justify-center flex-shrink-0 group-hover:bg-[rgba(45,107,104,0.15)] transition-colors">
                        <Icon className="w-5 h-5 text-[#2D6B68]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-medium text-[#1A1D1C] group-hover:text-[#2D6B68] transition-colors">{tpl.name}</h3>
                        <p className="text-xs text-[#6B7A73] mt-1">{sectionCount} section{sectionCount !== 1 ? 's' : ''} &middot; {tpl.project_bullets?.length || 0} scope items</p>
                        {tpl.project_bullets?.length > 0 && (
                          <p className="text-xs text-[#6B7A73] mt-2 line-clamp-2">{tpl.project_bullets[0]}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Custom Templates */}
          <div>
            <h2 className="text-xs uppercase tracking-[0.15em] font-semibold text-[#6B7A73] mb-4">Your Custom Templates</h2>
            {custom.length === 0 ? (
              <div className="bg-white border border-dashed border-[#C8D4CE] rounded-md p-8 text-center">
                <FileText className="w-10 h-10 text-[#E6EBE8] mx-auto mb-3" />
                <p className="text-sm text-[#6B7A73]">No custom templates yet.</p>
                <p className="text-xs text-[#6B7A73] mt-1">Create one to reuse your own estimate structures.</p>
                <Button onClick={openCreate} variant="outline" className="mt-4 border-[#E6EBE8] text-[#2D6B68]" data-testid="create-first-template-btn">
                  <Plus className="w-4 h-4 mr-2" /> Create Template
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {custom.map((tpl) => (
                  <div key={tpl.template_id} className="bg-white border border-[#E6EBE8] rounded-md p-5 hover:-translate-y-0.5 hover:border-[#C8D4CE] transition-all duration-200 cursor-pointer group relative" onClick={() => handleUseTemplate(tpl)} data-testid={`custom-template-${tpl.template_id}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-md bg-[rgba(45,107,104,0.1)] flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-[#2D6B68]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-medium text-[#1A1D1C]">{tpl.name}</h3>
                        <p className="text-xs text-[#6B7A73] mt-1">{tpl.sections?.length || 0} sections &middot; {tpl.project_bullets?.length || 0} scope items</p>
                      </div>
                    </div>
                    {/* Edit/Delete buttons */}
                    <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => openEdit(tpl, e)} className="p-1.5 rounded-md text-[#6B7A73] hover:text-[#2D6B68] hover:bg-[#F0F3F1] transition-colors" title="Edit" data-testid={`edit-template-${tpl.template_id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); setDeleteId(tpl.template_id); }} className="p-1.5 rounded-md text-[#6B7A73] hover:text-red-500 hover:bg-red-50 transition-colors" title="Delete" data-testid={`delete-template-${tpl.template_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Create/Edit Template Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) { setShowCreateDialog(false); resetForm(); } }}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>
              {editTemplate ? 'Edit Template' : 'Create Custom Template'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Name */}
            <div>
              <label className="text-xs text-[#6B7A73] block mb-1">Template Name</label>
              <Input data-testid="template-name-field" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Deck Construction" className="bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68]" />
            </div>

            {/* Scope Bullets */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-[#6B7A73]">Project Scope</label>
                <button onClick={() => setForm(prev => ({ ...prev, project_bullets: [...prev.project_bullets, ''] }))} className="text-xs text-[#2D6B68] font-medium" data-testid="add-template-bullet">+ Add</button>
              </div>
              {form.project_bullets.map((b, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input value={b} onChange={e => { const arr = [...form.project_bullets]; arr[i] = e.target.value; setForm(prev => ({ ...prev, project_bullets: arr })); }} placeholder="Scope item..." className="flex-1 bg-white border-[#E6EBE8] rounded-md text-sm" />
                  {form.project_bullets.length > 1 && (
                    <button onClick={() => setForm(prev => ({ ...prev, project_bullets: prev.project_bullets.filter((_, j) => j !== i) }))} className="text-[#6B7A73] hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              ))}
            </div>

            {/* Sections */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-[#6B7A73]">Sections</label>
                <button onClick={addFormSection} className="text-xs text-[#2D6B68] font-medium" data-testid="add-template-section">+ Add Section</button>
              </div>
              {form.sections.map((sec, sIdx) => (
                <div key={sIdx} className="border border-[#E6EBE8] rounded-md p-4 mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <Input value={sec.title} onChange={e => updateFormSection(sIdx, 'title', e.target.value)} placeholder="Section title" className="bg-transparent border-none p-0 h-auto focus:ring-0 text-sm font-medium" />
                    {form.sections.length > 1 && (
                      <button onClick={() => removeFormSection(sIdx)} className="text-[#6B7A73] hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                  {sec.items.map((item, iIdx) => (
                    <div key={iIdx} className="flex gap-2 mb-2">
                      <Textarea value={item.description} onChange={e => updateFormItem(sIdx, iIdx, 'description', e.target.value)} rows={2} placeholder="Line item description..." className="flex-1 bg-[#F9F9F8] border-[#E6EBE8] rounded-md text-xs resize-none" />
                      <button onClick={() => removeFormItem(sIdx, iIdx)} className="text-[#6B7A73] hover:text-red-500 self-start mt-2"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <button onClick={() => addFormItem(sIdx)} className="text-xs text-[#2D6B68] font-medium">+ Add Line Item</button>

                  {/* Notes */}
                  <div className="mt-3 pt-3 border-t border-[#E6EBE8]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] uppercase tracking-wider text-[#6B7A73] font-semibold">Notes</span>
                      <button onClick={() => updateFormSection(sIdx, 'contingencies', [...(sec.contingencies || []), ''])} className="text-[10px] text-[#6B7A73] font-medium">+ Add</button>
                    </div>
                    {(sec.contingencies || []).map((c, cIdx) => (
                      <div key={cIdx} className="flex gap-2 mb-1">
                        <Input value={c} onChange={e => { const arr = [...sec.contingencies]; arr[cIdx] = e.target.value; updateFormSection(sIdx, 'contingencies', arr); }} placeholder="Note..." className="flex-1 bg-[#F9F9F8] border-[#E6EBE8] rounded-md text-xs" />
                        <button onClick={() => updateFormSection(sIdx, 'contingencies', sec.contingencies.filter((_, j) => j !== cIdx))} className="text-[#6B7A73] hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Payment Terms */}
            <div>
              <label className="text-xs text-[#6B7A73] block mb-1">Payment Terms</label>
              <Textarea value={form.payment_terms} onChange={e => setForm(prev => ({ ...prev, payment_terms: e.target.value }))} rows={2} placeholder="e.g., 25% deposit required. Balance due upon completion." className="bg-white border-[#E6EBE8] rounded-md text-sm resize-none" />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }} className="border-[#E6EBE8]">Cancel</Button>
            <Button onClick={handleSaveTemplate} className="bg-[#2D6B68] text-white hover:bg-[#235552]" data-testid="save-template-form-btn">
              {editTemplate ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Delete Template?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B7A73]">This template will be permanently deleted. Existing estimates created from it won't be affected.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#E6EBE8]">Cancel</Button>
            <Button onClick={handleDeleteTemplate} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-template-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
